import React from "react";

import { useFormContext, useWatch } from "react-hook-form";

import { UserAccountType } from "@common/constants";
import { RadioInput } from "@components-shared/desktop/inputs";
import { useRootAppStore } from "@hooks";
import { rawFilter } from "@services/account-statement/AccountStatementService";
import { useAccountStatementStore } from "@state/hooks/account-statement/useAccountStatementStore";

export default function AccountStatementWalletFilter() {
	const rootStore = useRootAppStore();
	const form = useFormContext<rawFilter>();

	const store = useAccountStatementStore();

	const currentShopId = useWatch({
		name: "shopId",
	});

	if (
		(rootStore.userAuthStore.availableUserWalletsIds != null &&
			rootStore.userAuthStore.availableUserWalletsIds.length == 1) ||
		store == null
	) {
		return null;
	}

	const onlineWallet = rootStore.userAuthStore.availableWallets?.find(
		(type) =>
			type.abrv === UserAccountType.SportBettingAccountOnline &&
			(type.shopId == currentShopId ||
				(rootStore.userAuthStore.shopUserShops.has(currentShopId) &&
					type.shopId == rootStore.userAuthStore.defaultOnlineShopId))
	);

	const casinoWallet = rootStore.userAuthStore.availableWallets?.find(
		(type) =>
			type.abrv === UserAccountType.CasinoAccount &&
			(type.shopId == currentShopId ||
				(rootStore.userAuthStore.shopUserShops.has(currentShopId) &&
					type.shopId == rootStore.userAuthStore.defaultOnlineShopId))
	);

	const liveCasinoWallet = rootStore.userAuthStore.availableWallets?.find(
		(type) =>
			type.abrv === UserAccountType.LiveCasinoAccount &&
			(type.shopId == currentShopId ||
				(rootStore.userAuthStore.shopUserShops.has(currentShopId) &&
					type.shopId == rootStore.userAuthStore.defaultOnlineShopId))
	);

	const gamesWallet = rootStore.userAuthStore.availableWallets?.find(
		(type) =>
			type.abrv === UserAccountType.Games &&
			(type.shopId == currentShopId ||
				(rootStore.userAuthStore.shopUserShops.has(currentShopId) &&
					type.shopId == rootStore.userAuthStore.defaultOnlineShopId))
	);

	const shopOnlineWallet = rootStore.userAuthStore.availableWallets?.find(
		(type) =>
			type.abrv === UserAccountType.SportBettingAccountShopOnline &&
			(type.shopId == currentShopId ||
				(rootStore.userAuthStore.onlineUserShops.has(currentShopId) &&
					type.shopId ==
						rootStore.userAuthStore.activeShopUserShopId))
	);

	const offlineWallet = rootStore.userAuthStore.availableWallets?.find(
		(type) =>
			type.abrv === UserAccountType.SportBettingAccountShop &&
			(type.shopId == currentShopId ||
				(rootStore.userAuthStore.onlineUserShops.has(currentShopId) &&
					type.shopId ==
						rootStore.userAuthStore.activeShopUserShopId))
	);

	const visibleWallets = [
		onlineWallet,
		casinoWallet,
		liveCasinoWallet,
		gamesWallet,
		shopOnlineWallet,
		offlineWallet,
	].filter(Boolean);

	const handleWalletClick = (walletAbrv: string) => {
		if (
			store.shouldDisplayInitialLoader ||
			store.loader.isLoadingProcess ||
			store.transactionTableLoader.isLoadingProcess ||
			visibleWallets.length <= 1
		) {
			return;
		}

		if (form.getValues("bettingAccountTypeAbrv") == walletAbrv) {
			return;
		}

		form.setValue("bettingAccountTypeAbrv", walletAbrv);

		setFilterToDefault();
	};

	async function setFilterToDefault() {
		form.handleSubmit(async (data) => {
			const shopUserWallets: string[] = [
				UserAccountType.SportBettingAccountShop,
				UserAccountType.SportBettingAccountShopOnline,
			];

			let finalShopIdForFilter: string | undefined;

			const isShopUserWallet = shopUserWallets.includes(
				data.bettingAccountTypeAbrv
			);
			const relevantDefaultShopId = isShopUserWallet
				? rootStore.userAuthStore.activeShopUserShopId
				: rootStore.userAuthStore.defaultOnlineShopId;

			const matchedWallet =
				rootStore.userAuthStore.availableWallets?.find(
					(wallet) =>
						wallet.abrv === data.bettingAccountTypeAbrv &&
						wallet.shopId == form.getValues("shopId")
				);

			if (matchedWallet) {
				finalShopIdForFilter = matchedWallet.shopId;
			} else {
				finalShopIdForFilter =
					form.getValues("shopId") || relevantDefaultShopId || "";
			}

			data.shopId = finalShopIdForFilter || "";
			await store.onFilterSubmit({ ...data });
			if (store.statementFilter) {
				form.reset(store.statementFilter);
			}
		})();
	}

	if (visibleWallets.length === 1) {
		return null;
	}

	return (
		<div className="card--primary__filter no-print">
			<div className="card--primary__filter__list">
				{onlineWallet && (
					<div className="card--primary__filter__item">
						<RadioInput
							id="onlineWallet"
							name="bettingAccountTypeAbrv"
							value={onlineWallet.abrv}
							label="USER.WALLETS.ONLINE"
							onClick={() => handleWalletClick(onlineWallet.abrv)}
							wrapperClassname="input--radio--sml"
							inputClassName="input--radio--sml__box"
							labelClassname="card--primary__filter__label input--radio--sml__label"
							iconClass="u-icon u-icon--sml u-icon--wallet--online u-align--v--middle u-mar--right--xtny"
						/>
					</div>
				)}

				{casinoWallet && (
					<div className="card--primary__filter__item">
						<RadioInput
							id="casinoWallet"
							name="bettingAccountTypeAbrv"
							value={casinoWallet.abrv}
							label="USER.WALLETS.CASINO"
							onClick={() => handleWalletClick(casinoWallet.abrv)}
							wrapperClassname="input--radio--sml"
							inputClassName="input--radio--sml__box"
							labelClassname="card--primary__filter__label input--radio--sml__label"
							iconClass="u-icon u-icon--sml u-icon--wallet--casino u-align--v--middle u-mar--right--xtny"
						/>
					</div>
				)}

				{liveCasinoWallet && (
					<div className="card--primary__filter__item">
						<RadioInput
							id="liveCasinoWallet"
							name="bettingAccountTypeAbrv"
							value={liveCasinoWallet.abrv}
							label="USER.WALLETS.LIVE_CASINO"
							onClick={() =>
								handleWalletClick(liveCasinoWallet.abrv)
							}
							wrapperClassname="input--radio--sml"
							inputClassName="input--radio--sml__box"
							labelClassname="card--primary__filter__label input--radio--sml__label"
							iconClass="u-icon u-icon--sml u-icon--wallet--live-casino u-align--v--middle u-mar--right--xtny"
						/>
					</div>
				)}

				{gamesWallet && (
					<div className="card--primary__filter__item">
						<RadioInput
							id="gamesWallet"
							name="bettingAccountTypeAbrv"
							value={gamesWallet.abrv}
							label="USER.WALLETS.GAMES"
							onClick={() => handleWalletClick(gamesWallet.abrv)}
							wrapperClassname="input--radio--sml"
							inputClassName="input--radio--sml__box"
							labelClassname="card--primary__filter__label input--radio--sml__label"
							iconClass="u-icon u-icon--sml u-icon--wallet--games u-align--v--middle u-mar--right--xtny"
						/>
					</div>
				)}

				{shopOnlineWallet && (
					<div className="card--primary__filter__item">
						<RadioInput
							id="shopOnlineWallet"
							name="bettingAccountTypeAbrv"
							value={shopOnlineWallet.abrv}
							label="USER.WALLETS.SHOP"
							onClick={() =>
								handleWalletClick(shopOnlineWallet.abrv)
							}
							wrapperClassname="input--radio--sml"
							inputClassName="input--radio--sml__box"
							labelClassname="card--primary__filter__label input--radio--sml__label"
							iconClass="u-icon u-icon--sml u-icon--wallet--sport-betting-account-shop-online u-align--v--middle u-mar--right--xtny"
						/>
					</div>
				)}

				{offlineWallet && (
					<div className="card--primary__filter__item">
						<RadioInput
							id="offlineWallet"
							name="bettingAccountTypeAbrv"
							value={offlineWallet.abrv}
							label="USER.WALLETS.OFFLINE"
							onClick={() =>
								handleWalletClick(offlineWallet.abrv)
							}
							wrapperClassname="input--radio--sml"
							inputClassName="input--radio--sml__box"
							labelClassname="card--primary__filter__label input--radio--sml__label"
							iconClass="card--primary__filter__icon u-icon u-icon--sml u-icon--wallet--offline u-align--v--middle u-mar--right--xtny"
						/>
					</div>
				)}
			</div>
		</div>
	);
}
